import { Fragment, useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../lib/auth'
import { useConfirm } from '../lib/confirm'
import { useTheme } from '../lib/theme'
import { useCapabilities } from '../lib/capabilities'
import { api } from '../lib/api'
import { portalFor, portalLabel } from '../lib/roles'
import type { ListResponse, AppNotification, Organization } from '../types'
import { Logo } from './Logo'
import OfflineBar from './OfflineBar'
import { Avatar } from './ui'
import {
  AlertIcon, BellIcon, ChartIcon, CheckIcon, CloseIcon, CoinIcon, DocIcon,
  GradCapIcon, HomeIcon, IdIcon, LogOutIcon, MenuIcon, MoonIcon, RouteIcon,
  ShieldIcon, SunIcon, TruckIcon, UserIcon, UsersGroupIcon, WrenchIcon,
} from './icons'
import './layout.css'

interface NavItem {
  to: string
  label: string
  icon: (props: { size?: number }) => React.ReactElement
  end?: boolean
  /** Shows the unread-notification count beside the item. */
  badge?: boolean
  /** Badges this item with a live count from GET /me/queue-counts. Every
   * queue carries one so a person can see, from the menu, that something is
   * waiting on them - the counts are computed per-viewer server-side, so a
   * badge never hints at work they may not see (e.g. private works). */
  countKey?: string
  /** Only shown when the signed-in user holds this capability
   * (role_service.CAPABILITIES) - mirrors home_bp's _ACTIONS gating, so
   * custom roles from the Role Builder pick up the right tools automatically. */
  cap?: string
  /** Only shown when a super admin has granted this user sight of at least
   * one individual truck/equipment/load. Unlike `cap`, this is data-driven,
   * not capability-driven - the grant recipient may hold no operational
   * capability at all, so there is no capability key to gate on. */
  needsResourceGrant?: boolean
  /** Category heading this item sits under in the PWA's snsmenu (rendered
   * whenever it differs from the previous VISIBLE item's section, so a
   * heading never shows for a group whose items were all filtered out by
   * cap/grant gating). Desktop's sidebar stays a flat list - untouched. */
  section?: string
}

const MINER_NAV: NavItem[] = [
  { to: '/', section: 'Main',                 label: 'Dashboard',       icon: HomeIcon, end: true },
  { to: '/production', section: 'Main',       label: 'Production',      icon: ChartIcon, cap: 'management.senior' },
  { to: '/safety', section: 'Main',           label: 'Safety',          icon: AlertIcon },
  { to: '/compliance', section: 'Main',       label: 'Compliance',      icon: ShieldIcon },
  { to: '/kyc', section: 'My account',              label: 'My documents',    icon: IdIcon },
  { to: '/learning', section: 'Training',         label: 'Training',        icon: GradCapIcon },
  { to: '/permits', section: 'Tools',          label: 'Work permits',    icon: DocIcon, cap: 'permits.manage' },
  { to: '/safety-documents', section: 'Tools', label: 'Safety documents', icon: DocIcon, cap: 'documents.manage' },
  { to: '/training-manage', section: 'Tools',  label: 'Manage training',  icon: GradCapIcon, cap: 'training.manage' },
  { to: '/daily-safety-report', section: 'Tools', label: 'Daily safety report', icon: AlertIcon, cap: 'report.daily_safety' },
  { to: '/dispatch', section: 'Tools',         label: 'Loads',            icon: RouteIcon, cap: 'dispatch.manage' },
  { to: '/equipment', section: 'Tools',        label: 'Equipment',        icon: WrenchIcon, cap: 'dispatch.manage' },
  { to: '/dispatch-closure', section: 'Tools', label: 'Dispatch closure', icon: RouteIcon, cap: 'management.senior', countKey: 'dispatch_closure' },
  { to: '/log-expense', section: 'Tools',      label: 'Log expense',      icon: CoinIcon, cap: 'expense.log' },
  { to: '/sales', section: 'Tools',            label: 'Sales',            icon: CoinIcon, cap: 'sales.log' },
  { to: '/finance', section: 'Tools',          label: 'Finance',          icon: ChartIcon, cap: 'reports.view.sensitive' },
  { to: '/hr', section: 'Tools',               label: 'Staff records',    icon: UsersGroupIcon, cap: 'hr.view' },
  { to: '/hr/payroll', section: 'Tools',       label: 'Payroll',          icon: CoinIcon, cap: 'hr.manage' },
  { to: '/gate', section: 'Tools',             label: 'Gate verification', icon: ShieldIcon, cap: 'security.gate_verify' },
  { to: '/approvals', section: 'Tools',        label: 'Approvals',        icon: CheckIcon, cap: 'approvals.review', countKey: 'approvals' },
  { to: '/shared-with-me', section: 'Tools',   label: 'Shared with me',   icon: TruckIcon, needsResourceGrant: true },
  { to: '/reports', section: 'Tools',          label: 'Reports',          icon: ChartIcon, cap: 'reports.view.operations' },
  { to: '/notifications', section: 'My account',    label: 'Notifications',   icon: BellIcon, badge: true },
]

const LEADER_NAV: NavItem[] = [
  { to: '/', section: 'Main',              label: 'Overview',      icon: HomeIcon, end: true },
  { to: '/onboarding', section: 'Main',    label: 'Compliance setup', icon: ShieldIcon },
  { to: '/production', section: 'Operations',    label: 'Production',    icon: ChartIcon },
  { to: '/kyc', section: 'People',           label: 'Verification',  icon: IdIcon },
  { to: '/safety', section: 'Safety & compliance',        label: 'Safety triage', icon: AlertIcon },
  { to: '/team', section: 'People',          label: 'Team',          icon: UserIcon },
  { to: '/sites', section: 'Operations',         label: 'Sites',         icon: IdIcon },
  { to: '/organization', section: 'Main',  label: 'Organisation',  icon: UsersGroupIcon },
  { to: '/trucks', section: 'Operations',        label: 'Fleet',         icon: TruckIcon },
  { to: '/equipment', section: 'Operations',     label: 'Equipment',     icon: WrenchIcon },
  { to: '/loads', section: 'Operations',         label: 'Loads',         icon: RouteIcon },
  { to: '/expenses', section: 'Operations',      label: 'Expenses',      icon: CoinIcon },
  { to: '/reports', section: 'More',       label: 'Reports',       icon: ChartIcon },
  { to: '/learning', section: 'Training',      label: 'Training',      icon: GradCapIcon },
  { to: '/compliance', section: 'Safety & compliance',    label: 'Compliance',    icon: ShieldIcon },
  { to: '/permits', section: 'Safety & compliance',          label: 'Work permits',    icon: DocIcon, cap: 'permits.manage' },
  { to: '/safety-documents', section: 'Safety & compliance', label: 'Safety documents', icon: DocIcon, cap: 'documents.manage' },
  { to: '/training-manage', section: 'Training',  label: 'Manage training',  icon: GradCapIcon, cap: 'training.manage' },
  { to: '/daily-safety-report', section: 'Safety & compliance', label: 'Daily safety report', icon: AlertIcon, cap: 'report.daily_safety' },
  { to: '/sales', section: 'Finance & HR',            label: 'Sales',            icon: CoinIcon, cap: 'sales.log' },
  { to: '/finance', section: 'Finance & HR',          label: 'Finance',          icon: ChartIcon, cap: 'reports.view.sensitive' },
  { to: '/hr', section: 'Finance & HR',               label: 'Staff records',    icon: UsersGroupIcon, cap: 'hr.view' },
  { to: '/hr/payroll', section: 'Finance & HR',       label: 'Payroll',          icon: CoinIcon, cap: 'hr.manage' },
  { to: '/gate', section: 'More',             label: 'Gate verification', icon: ShieldIcon, cap: 'security.gate_verify' },
  { to: '/approvals', section: 'More',        label: 'Approvals',        icon: CheckIcon, cap: 'approvals.review', countKey: 'approvals' },
  { to: '/shared-with-me', section: 'More',   label: 'Shared with me',   icon: TruckIcon, needsResourceGrant: true },
  { to: '/notifications', section: 'More', label: 'Notifications', icon: BellIcon, badge: true },
]

const GOV_NAV: NavItem[] = [
  { to: '/', section: 'Main',                  label: 'Overview',        icon: HomeIcon, end: true },
  { to: '/sites', section: 'Field work',              label: 'Sites',           icon: IdIcon },
  { to: '/inspections', section: 'Field work',        label: 'Inspections',     icon: CheckIcon },
  { to: '/notices', section: 'Field work',            label: 'Notices',         icon: DocIcon },
  { to: '/licences', section: 'Field work',           label: 'Licences',        icon: ShieldIcon },
  { to: '/production', section: 'Resources',         label: 'Production',      icon: ChartIcon },
  { to: '/parcels', section: 'Resources',            label: 'Land parcels',    icon: IdIcon },
  { to: '/checklists', section: 'Field work',         label: 'Checklists',      icon: CheckIcon },
  { to: '/course-uploads', section: 'Training',     label: 'Course uploads',  icon: GradCapIcon },
  { to: '/course-progress', section: 'Training',    label: 'Training',        icon: GradCapIcon },
  { to: '/equipment', section: 'Resources',          label: 'Equipment',       icon: WrenchIcon },
  { to: '/users', section: 'People',              label: 'Users & orgs',    icon: UsersGroupIcon },
  { to: '/unallocated-miners', section: 'People', label: 'Unallocated',     icon: UserIcon },
]

const ADMIN_NAV: NavItem[] = [
  { to: '/', section: 'Main',             label: 'Dashboard',     icon: HomeIcon, end: true },
  { to: '/users', section: 'People & orgs',        label: 'Users',         icon: UsersGroupIcon },
  { to: '/organizations', section: 'People & orgs', label: 'Organisations', icon: IdIcon },
  { to: '/compliance', section: 'Review',   label: 'Compliance',    icon: CheckIcon },
  { to: '/approvals', section: 'Review',    label: 'Approvals',     icon: DocIcon, countKey: 'approvals' },
  { to: '/role-catalog', section: 'Roles', label: 'Role Catalog',  icon: ShieldIcon },
  { to: '/roles', section: 'Roles',        label: 'Role Builder',  icon: WrenchIcon },
  { to: '/hr', section: 'Finance & HR',           label: 'Staff records', icon: UserIcon },
  { to: '/hr/payroll', section: 'Finance & HR',   label: 'Payroll',       icon: CoinIcon },
  { to: '/finance', section: 'Finance & HR',      label: 'Finance',       icon: ChartIcon },
  { to: '/community-levy', section: 'Finance & HR', label: 'Community levy', icon: CoinIcon },
  { to: '/sites', section: 'Operations',        label: 'Sites',         icon: IdIcon },
  { to: '/dispatch-closure', section: 'Operations', label: 'Dispatch closure', icon: RouteIcon, countKey: 'dispatch_closure' },
  { to: '/loads', section: 'Operations',        label: 'Loads',         icon: RouteIcon },
  { to: '/trucks', section: 'Operations',       label: 'Fleet',         icon: TruckIcon },
  { to: '/expenses', section: 'Operations',     label: 'Expenses',      icon: CoinIcon },
  { to: '/hired-equipment', section: 'Operations', label: 'Equipment',  icon: WrenchIcon },
  { to: '/learning', section: 'Training',     label: 'Training',      icon: GradCapIcon },
  { to: '/course-progress', section: 'Training', label: 'Training progress', icon: GradCapIcon },
  { to: '/private', section: 'People & orgs',      label: 'Private users',  icon: ShieldIcon },
  { to: '/unallocated-miners', section: 'People & orgs', label: 'Unallocated', icon: UserIcon },
  { to: '/users-map', section: 'People & orgs',    label: 'Users map',     icon: IdIcon },
  { to: '/equipment-guide', section: 'Operations', label: 'Equipment guide', icon: WrenchIcon },
  { to: '/permissions', section: 'Roles',   label: 'Permissions',   icon: ShieldIcon },
]

/** Admin-portal destinations reserved for the super admin. A deputy
 * "admin" keeps the operational and people screens but never the ones that
 * change the platform's own shape or show money. */
const DEPUTY_ADMIN_HIDDEN = new Set([
  '/role-catalog', '/roles', '/permissions',   // platform shape
  '/private',                                   // private works
  '/finance', '/hr/payroll', '/community-levy', // money
])

/* Primary destinations for the fixed mobile bottom bar - deliberately just
 * 3 real ones + a trailing "Menu" tab that opens the same drawer the header
 * burger does, rather than trying to fit the full portal nav down here.
 * `end` mirrors NavLink's own exact-match option so "Home" doesn't stay lit
 * on every sub-route. */
interface BottomNavItem { to: string; label: string; icon: (props: { size?: number }) => React.ReactElement; end?: boolean }

const MINER_BOTTOM: BottomNavItem[] = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/production', label: 'Production', icon: ChartIcon },
  { to: '/safety', label: 'Safety', icon: AlertIcon },
]
const LEADER_BOTTOM: BottomNavItem[] = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/reports', label: 'Reports', icon: ChartIcon },
  { to: '/trucks', label: 'Fleet', icon: TruckIcon },
]
const GOV_BOTTOM: BottomNavItem[] = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/sites', label: 'Sites', icon: IdIcon },
  { to: '/inspections', label: 'Inspections', icon: CheckIcon },
]
const ADMIN_BOTTOM: BottomNavItem[] = [
  { to: '/', label: 'Home', icon: HomeIcon, end: true },
  { to: '/users', label: 'Users', icon: UsersGroupIcon },
  { to: '/organizations', label: 'Orgs', icon: IdIcon },
]

export default function AppLayout() {
  const { user, signOut } = useAuth()
  const { toggle } = useTheme()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const location = useLocation()
  const [navOpen, setNavOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close the drawer on navigation so a tap-through on mobile doesn't leave it open.
  useEffect(() => { setNavOpen(false); setMenuOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const portal = portalFor(user)
  const capabilities = useCapabilities()
  const hasResourceGrant = Boolean(
    user?.truck_access?.length || user?.equipment_access?.length || user?.load_access?.length,
  )
  // The deputy "admin" role shares the admin portal but not its whole
  // sidebar: anything that reshapes the platform itself (roles, permissions,
  // privacy) or exposes money stays with the super admin. Mirrors the
  // server-side split between _require_super_admin and _require_admin_portal.
  const isDeputyAdmin = portal === 'admin' && user?.role !== 'super_admin'
  const adminNav = isDeputyAdmin
    ? ADMIN_NAV.filter((item) => !DEPUTY_ADMIN_HIDDEN.has(item.to))
    : ADMIN_NAV
  const nav = (portal === 'gov' ? GOV_NAV : portal === 'admin' ? adminNav : portal === 'leader' ? LEADER_NAV : MINER_NAV)
    .filter((item) => !item.cap || capabilities.has(item.cap))
    .filter((item) => !item.needsResourceGrant || hasResourceGrant)
  const bottomNav = portal === 'gov' ? GOV_BOTTOM : portal === 'admin' ? ADMIN_BOTTOM : portal === 'leader' ? LEADER_BOTTOM : MINER_BOTTOM

  // The current screen's name for the mobile app-bar title - the longest
  // matching nav `to` wins (so /users/:uid still reads "Users", not falls
  // through to nothing), and the root route shows the brand instead of
  // repeating "Home".
  const pageTitle = (() => {
    if (location.pathname === '/') return null
    const match = [...nav].filter((item) => (
      item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)
    )).sort((a, b) => b.to.length - a.to.length)[0]
    return match?.label ?? null
  })()

  // Only the 'leader' portal label depends on org type (a cooperative's and a
  // company's leader both hold role 'company', see roles.ts) - same query key
  // Organization.tsx/StaffBadge.tsx use, so this shares their cache rather
  // than firing a second request when either of those pages is also open.
  const { data: orgData } = useQuery({
    queryKey: ['leader', 'org', user?.org_id],
    queryFn: () => api.get<{ organization: Organization }>(`/organizations/${user?.org_id}`),
    enabled: portal === 'leader' && Boolean(user?.org_id),
  })

  // Notifications are served per-portal; a leader hitting /miner/notifications
  // would get a 403 rather than their own queue. There is no /gov equivalent,
  // so the bell is hidden for regulators rather than pointed at someone else's.
  const notifBase = portal === 'leader' ? '/leader' : '/miner'
  const { data: notifs } = useQuery({
    queryKey: ['notifications', portal],
    queryFn: () => api.get<ListResponse<AppNotification>>(`${notifBase}/notifications`),
    refetchInterval: 120_000,
    enabled: portal !== 'gov' && portal !== 'admin',
  })
  const unread = notifs?.unread ?? 0

  // One request feeds every queue badge in the menu.
  const { data: queueData } = useQuery({
    queryKey: ['queue-counts'],
    queryFn: () => api.get<{ ok: boolean; counts: Record<string, number> }>('/me/queue-counts'),
    refetchInterval: 120_000,
  })
  const queueCounts = queueData?.counts ?? {}
  const countFor = (countKey?: string, badge?: boolean) =>
    countKey ? (queueCounts[countKey] ?? 0) : (badge ? unread : 0)
  const registrationStatus = orgData?.organization.registration_status
  const isPending = portal === 'leader' && registrationStatus && registrationStatus !== 'approved'

  async function onSignOut() {
    if (!(await confirm({ title: 'Sign out?', message: "You'll need to sign back in to continue." }))) return
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div className={`shell${navOpen ? ' shell--menu-open' : ''}`}>
      {/* ShrinkNSlide side menu (PWA only - hidden ≥1024px, where the
          regular .side sidebar takes over). Mirrors the Flutter app's
          shrink_sidemenu ShrinkNSlide type: the menu sits UNDER the page on
          the brand green, and opening shrinks + slides the whole page right
          to reveal it (see .snsmenu/.page in layout.css). Tapping the
          shrunken page anywhere closes it. */}
      <aside className="snsmenu" aria-hidden={!navOpen}>
        <button
          type="button" className="snsmenu__close"
          onClick={() => setNavOpen(false)}
          aria-label="Close menu"
          tabIndex={navOpen ? 0 : -1}
        >
          <CloseIcon size={18} />
        </button>
        <div className="snsmenu__head">
          <Avatar className="snsmenu__avatar" photoUrl={user?.photo_url} name={user?.full_name} email={user?.email} />
          <div className="snsmenu__name">{user?.full_name || user?.email}</div>
          {user?.email && <div className="snsmenu__email">{user.email}</div>}
          <span className="snsmenu__role">{(user?.role ?? 'miner').replace(/_/g, ' ')}</span>
        </div>
        <nav className="snsmenu__nav">
          {/* Group by section (sections keep first-appearance order, items
              keep nav order within their group). `nav` is already cap/grant
              filtered, so an all-hidden group never leaves a stray heading.
              Grouping happens HERE, not in the arrays, so the desktop
              sidebar's flat order is untouched. */}
          {[...new Set(nav.map((n) => n.section ?? ''))].map((section) => (
            <Fragment key={section || 'general'}>
              {section && <div className="snsmenu__section">{section}</div>}
              {nav.filter((n) => (n.section ?? '') === section).map(({ to, label, icon: Icon, end, badge, countKey }) => (
                <NavLink
                  key={to} to={to} end={end}
                  onClick={() => setNavOpen(false)}
                  className={({ isActive }) => `snsmenu__link${isActive ? ' snsmenu__link--active' : ''}`}
                  tabIndex={navOpen ? 0 : -1}
                >
                  <span className="snsmenu__icon"><Icon size={18} /></span>
                  {label}
                  {countFor(countKey, badge) > 0 && (
                    <span className="nav__count">{countFor(countKey, badge)}</span>
                  )}
                </NavLink>
              ))}
            </Fragment>
          ))}
        </nav>
        <div className="snsmenu__foot">
          <Link to="/profile" className="snsmenu__link" onClick={() => setNavOpen(false)} tabIndex={navOpen ? 0 : -1}>
            <span className="snsmenu__icon"><UserIcon size={18} /></span>
            Profile &amp; settings
          </Link>
          <button className="snsmenu__link snsmenu__link--danger" onClick={onSignOut} tabIndex={navOpen ? 0 : -1}>
            <span className="snsmenu__icon"><LogOutIcon size={18} /></span>
            Sign out
          </button>
        </div>
      </aside>

      <div
        className={`page${navOpen ? ' page--shrunk' : ''}`}
        onClickCapture={navOpen ? (e) => { e.preventDefault(); e.stopPropagation(); setNavOpen(false) } : undefined}
      >
      <header className="hdr">
        <button
          className="hdr__burger"
          onClick={() => setNavOpen((o) => !o)}
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
        >
          {navOpen ? <CloseIcon /> : <MenuIcon />}
        </button>

        <Link
          to="/"
          className={pageTitle ? 'hdr__brand hdr__brand--hide-mobile' : 'hdr__brand'}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Logo />
        </Link>
        {/* Mobile-only app-bar title for the current screen - the sidebar's
            own section heading already does this job on desktop, so this
            stays hidden there (see .hdr__title in layout.css). */}
        {pageTitle && <h1 className="hdr__title">{pageTitle}</h1>}

        <div className="hdr__spacer" />

        <button className="icon-btn" onClick={toggle} aria-label="Toggle colour theme">
          <span className="only-light"><MoonIcon /></span>
          <span className="only-dark"><SunIcon /></span>
        </button>

        {portal !== 'gov' && portal !== 'admin' && (
          <Link to="/notifications" className="icon-btn"
                aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}>
            <BellIcon />
            {unread > 0 && <span className="icon-btn__dot">{unread > 9 ? '9+' : unread}</span>}
          </Link>
        )}

        <div className="usr" ref={menuRef}>
          <button
            className="usr__btn"
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Avatar className="usr__avatar" photoUrl={user?.photo_url} name={user?.full_name} email={user?.email} />
            <span className="usr__name">{user?.full_name || user?.email}</span>
          </button>

          {menuOpen && (
            <div className="menu" role="menu">
              <div className="menu__head">
                <div className="menu__email">{user?.email}</div>
                <div className="menu__role">
                  {(user?.role ?? 'miner').replace(/_/g, ' ')}
                  {user?.org_id ? ' · member' : ''}
                </div>
              </div>
              <Link to="/profile" className="menu__item" role="menuitem">
                <UserIcon size={16} /> Profile &amp; settings
              </Link>
              <button className="menu__item menu__item--danger" onClick={onSignOut} role="menuitem">
                <LogOutIcon size={16} /> Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="body">
        {/* Desktop-only sidebar - the snsmenu above owns navigation ≤1023px
            (see .side's display:none there), so the old slide-over drawer +
            scrim are gone and navOpen no longer touches this element. */}
        <aside className="side">
          <div className="side__label">{portalLabel(portal, orgData?.organization.type)}</div>
          {/* Same section grouping as the PWA menu above, so a person moving
              between phone and desktop meets one navigation structure rather
              than a categorised list on one and a flat list on the other. */}
          <nav className="nav">
            {[...new Set(nav.map((n) => n.section ?? ''))].map((section) => (
              <Fragment key={section || 'general'}>
                {section && <div className="nav__section">{section}</div>}
                {nav.filter((n) => (n.section ?? '') === section).map(({ to, label, icon: Icon, end, badge, countKey }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) => `nav__link${isActive ? ' nav__link--active' : ''}`}
                  >
                    <span className="nav__icon"><Icon /></span>
                    {label}
                    {countFor(countKey, badge) > 0 && (
                      <span className="nav__count">{countFor(countKey, badge)}</span>
                    )}
                  </NavLink>
                ))}
              </Fragment>
            ))}
          </nav>
        </aside>

        <main className="main">
          {/* Connectivity first: if the device is offline or work is waiting
              to be sent, that outranks anything else on the screen. */}
          <OfflineBar />
          {isPending && (
            <div className="pending-banner" role="status">
              <AlertIcon size={16} />
              <span>
                Your organisation is awaiting approval and can't yet dispatch loads or pass staff through
                security until a super admin approves it.
              </span>
              <Link to="/onboarding" className="pending-banner__link">View status</Link>
            </div>
          )}
          <div className="main__inner"><Outlet /></div>
        </main>
      </div>

      <nav className="bottom-nav" aria-label="Primary">
        {bottomNav.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `bottom-nav__item${isActive ? ' bottom-nav__item--active' : ''}`}
          >
            <Icon size={20} />
            {label}
          </NavLink>
        ))}
        {/* The trailing "Menu" tab is gone - the header burger already
            owns the drawer, and duplicating it here just ate a slot. */}
      </nav>
      </div>
    </div>
  )
}
